import type { SuperDocInstance, SuperDocRuntimeEditor } from '@superdoc/react';
import { cleDeMontage, type CanvasDraft } from '@hector/shared-types';
import { decrireLeChargementDeLEditeur } from '../editor/chargerLEditeur';
import {
  CanvasSessionRegistry,
  canvasSessionRegistry,
  type CanvasSessionCallbackIdentity,
  type CanvasSessionRegistrySnapshot,
  type CanvasSessionSnapshot,
  isCanvasSessionProtected,
} from './canvasSessionRegistry';
import {
  CanvasDocumentScope,
  CanvasDocumentScopeDirectory,
  presentationDUnBrouillonRestaure,
  type CanvasDocumentMountIdentity,
  type CanvasDocumentPresentationSnapshot,
  type CanvasDocumentScopeDirectoryOptions,
  type CanvasDocumentScopeSnapshot,
} from './canvasDocumentScope';
import type { CanvasDraftSessionController } from './canvasDraftController';
import { canvasDraftApi } from '../api/canvasDraft.api';
import { estEnCoursDeSuppression } from './filsEnSuppression';
import {
  CanvasResidenceCapacityCoordinator,
  type CanvasResidenceCapacityOptions,
  type CanvasResidentDecision,
  type CanvasResidentRequest,
  type CanvasResidentSeed,
} from './canvasResidenceCapacity';

export type CanvasRuntimeSessionState = 'loading' | 'ready' | 'failed';
export type CanvasRuntimeSession = Omit<CanvasDocumentScopeSnapshot, 'state'> & {
  state: CanvasRuntimeSessionState;
};

/**
 * Preuve locale qu'un flux de rédaction a réellement fini de poser ses blocs
 * dans l'éditeur SuperDoc du montage exact.
 *
 * Ce n'est pas le terminal HTTP/SSE : celui-ci peut arriver avant le dernier
 * rendu React qui place son delta dans `PointDEcritureCanvas` (#1606).
 */
export type CanvasStreamInsertionSettled = {
  readonly conversationId: string;
  readonly mountEpoch: number;
  readonly jobId: string;
};

/** Observation technique d'une écriture retenue par un export déjà démarré. */
export type CanvasExportWaitEvent =
  | { readonly stage: 'started' }
  | { readonly stage: 'completed'; readonly durationMs: number };

/** Hooks optionnels : ils ne doivent jamais modifier l'activité documentaire observée. */
export interface CanvasActivityOptions {
  readonly onExportWait?: (event: CanvasExportWaitEvent) => void;
}

/** Le seul contrat public qui remplace un document déjà résident. */
export type CanvasRuntimeDocumentReplacementInput = {
  readonly identity: CanvasSessionCallbackIdentity;
  readonly documentId: string | null;
  readonly presentation: CanvasDocumentPresentationSnapshot;
  readonly signal?: AbortSignal;
  /** Reservation held across an async prerequisite such as draft deletion. */
  readonly replacementLease?: CanvasExclusiveReplacementLease;
};

/**
 * Pourquoi un remplacement exact n'a pas eu lieu.
 *
 * ⚠️ **`conversation-mismatch` a disparu, et c'était une tautologie** (#1336) :
 * la session courante est TROUVÉE par la conversation du geste, donc comparer
 * ensuite son propriétaire à celui que le geste annonce rendrait la même valeur
 * avec le défaut et sans lui. `mount-not-started` la remplace — elle vient de
 * `decideCallback` et refuse un geste capturé avant tout montage.
 */
export type CanvasRuntimeDocumentReplacementDecision =
  | { readonly accepted: true; readonly session: CanvasRuntimeSession; readonly previousMountEpoch: number }
  | {
    readonly accepted: false;
    readonly reason:
      | 'session-not-found'
      | 'mount-not-started'
      | 'mount-epoch-mismatch'
      | 'session-not-mounted'
      | 'session-protected'
      | 'replacement-in-progress'
      | 'aborted';
  };

/** Opaque runtime reservation; only the coordinator can create a valid lease. */
export type CanvasExclusiveReplacementLease = (() => void) & {
  readonly conversationId: string;
  readonly leaseId: number;
  readonly release: () => void;
};

/**
 * Le montage qu'un appelant attend : CELUI-CI, portant CE document (D3, #1337).
 *
 * Deux questions distinctes, toutes deux posées :
 *  - `mountEpoch` — « est-ce le montage que MON remplacement a alloué ? ». Une
 *    égalité, jamais un « postérieur à » : une comparaison d'ordre accepterait
 *    le premier montage venu après le mien, quel que soit son contenu — sur un
 *    canvas généré (`documentId` nul) plus rien ne les distinguerait. Une époque
 *    n'est jamais réémise (`nextMountEpoch`), donc elle désigne un seul montage.
 *  - `documentId` — « porte-t-il le document que J'AI demandé ? ». Il vient de
 *    la DEMANDE de l'appelant, jamais du montage qu'il juge : relu sur ce même
 *    montage, il rendrait la même valeur avec le défaut et sans lui.
 *
 * Il n'y a plus de repli sur la conversation visible : une attente sans
 * identité était « pas de contrainte », et le dernier routage implicite vers
 * « le document qu'on regarde » (#1336).
 */
export type CanvasReadyExpectation = {
  readonly conversationId: string;
  readonly mountEpoch: number;
  /** Le document demandé ; `null` pour un canvas généré ou vierge. */
  readonly documentId: string | null;
};

/**
 * Une raison pour laquelle le document d'une conversation n'est pas au repos
 * (`decrireLOccupationDuDocument`, #1586).
 *
 * Deux familles, que les appelants ne traitent pas pareil : `monte`, `chaud`,
 * `hydratation`, `transaction-de-residence` et `remplacement-exclusif` disent
 * qu'un éditeur existe ou va exister — y poser un brouillon reconstruirait
 * SuperDoc sous une époque neuve. `epingle` et `activite` disent qu'un travail
 * tient le fil, ce qui n'interdit rien tant qu'aucun document logique n'existe.
 */
export type CauseDOccupationDocumentaire =
  | 'monte'
  | 'chaud'
  | 'hydratation'
  | 'transaction-de-residence'
  | 'remplacement-exclusif'
  | 'epingle'
  | 'activite';

/** Un montage prêt : `readyMatch` n'en rend jamais un sans révision lisible. */
export type CanvasRuntimeReadySession = CanvasRuntimeSession & { readonly revision: string };

type ReadyWaiter = {
  expected: CanvasReadyExpectation;
  timeoutId: ReturnType<typeof setTimeout>;
  resolve: (session: CanvasRuntimeReadySession) => void;
  reject: (error: Error) => void;
};

type RuntimeOptions = {
  registry?: CanvasSessionRegistry;
  scopeDirectory?: CanvasDocumentScopeDirectory;
  draftControllerFactory?: NonNullable<CanvasDocumentScopeDirectoryOptions['draftControllerFactory']>;
  capacity?: CanvasResidenceCapacityOptions;
};

function toRuntimeSession(snapshot: CanvasDocumentScopeSnapshot): CanvasRuntimeSession | null {
  if (snapshot.state === 'disposed') return null;
  return { ...snapshot, state: snapshot.state };
}

/**
 * Clé composite d'un montage réglé.
 *
 * ⚠️ **Jamais recomposée à la main** : `cleDeMontage` vit dans
 * `@hector/shared-types` et sert aussi le `snapshotToken` de la chaîne agent.
 * Deux compositions divergeraient le jour où la forme de l'identité changerait,
 * et le symptôme serait un montage réglé deux fois — donc un listener `ready`
 * qui refire sur un montage déjà annoncé.
 */
function cleDunMontageRegle(identity: CanvasSessionCallbackIdentity): string {
  return cleDeMontage({
    forme: 'conversation',
    conversationId: identity.conversationId,
    mountEpoch: identity.mountEpoch,
  });
}

/**
 * Façade d'orchestration des scopes documentaires.
 *
 * Le directory est la source de vérité des ressources : chaque opération qui
 * peut atteindre SuperDoc exige la CONVERSATION qui possède le document
 * (#1336). La notion de session sélectionnée n'existe plus qu'à UN endroit,
 * `visibleConversationId` dans le registre ; elle projette l'unique host visible
 * parmi les résidents chauds et n'est jamais une destination de routage agent.
 */
export class CanvasDocumentRuntimeCoordinator {
  private readonly waiters = new Set<ReadyWaiter>();
  private readonly registry: CanvasSessionRegistry;
  private readonly directory: CanvasDocumentScopeDirectory;
  private readonly readyListeners = new Set<(session: CanvasRuntimeSession) => void>();
  private readonly settledListeners = new Set<(session: CanvasRuntimeSession) => void>();
  private readonly streamInsertionSettledListeners = new Set<(event: CanvasStreamInsertionSettled) => void>();
  /** Notified synchronously after a scope owns a DOM host, before React mounts SuperDoc into it. */
  private readonly scopeMountedListeners = new Set<(conversationId: string) => void>();
  /**
   * Cold mounts whose exact seed is held by a caller while the assistant slot
   * commits. CanvasResidence must not race them with its intentionally blank
   * fallback seed.
   */
  private readonly coldMountsAwaitingVisibleSlot = new Map<string, symbol>();
  private readonly observedScopes = new WeakSet<CanvasDocumentScope>();
  private readonly observedDraftControllers = new WeakSet<CanvasDraftSessionController>();
  // Controllers outlive physical mounts. Neither cooling nor clearDraft may
  // erase evidence that an editor or draft could contain work beyond its seed.
  private readonly seedReuseForbidden = new WeakSet<CanvasDraftSessionController>();
  /**
   * Contrôleurs dont le document a pu RECEVOIR du travail : un montage devenu
   * prêt, un brouillon qui a bougé, ou une histoire que ce runtime n'a pas
   * observée. Sous-ensemble strict de `seedReuseForbidden`, qui retient en plus
   * la simple EXPOSITION d'un éditeur.
   *
   * Les deux mémoires répondent à deux questions (défaut du 2026-10-09) :
   * « peut-on REJOUER l'ouverture retenue ? » — non dès qu'un éditeur a existé,
   * la source pouvant être celle qui le fait échouer ; « peut-on monter AUTRE
   * CHOSE à sa place sans rien perdre ? » — oui tant qu'aucun montage n'a été
   * prêt : un éditeur créé puis tombé en échec avant `ready` (un Word dont
   * `doc.info` jette) n'a jamais été écrivable, ni par l'avocat ni par l'agent.
   * Une frappe dans cet éditeur passerait par `markDirty`, donc par
   * `projectCheckpoint`, qui l'inscrit ici.
   */
  private readonly aPuRecevoirDuTravail = new WeakSet<CanvasDraftSessionController>();
  /** Clés `cleDeMontage({forme:'conversation', …})`, jamais une conversation nue. */
  private readonly settledMounts = new Set<string>();
  /** Clé `(conversationId, mountEpoch, jobId)` des fins d'insertion déjà vues. */
  private readonly settledStreamInsertions = new Set<string>();
  private readonly capacity: CanvasResidenceCapacityCoordinator;
  /** Clé = `conversationId` ; valeur = identifiant du bail exclusif en cours. */
  private readonly exclusiveReplacements = new Map<string, number>();
  /** Authentic pin handles; opaque identity prevents a caller from forging its own exemption. */
  private livePins = new WeakMap<() => void, string>();
  private nextActivityToken = 0;

  constructor(options: RuntimeOptions = {}) {
    this.registry = options.registry ?? new CanvasSessionRegistry();
    this.directory = options.scopeDirectory ?? new CanvasDocumentScopeDirectory({
      draftControllerFactory: options.draftControllerFactory,
      // #1404 — write_markdown, génération orchestrateur et verbes agent
      // déclarent leur activité au registre ; l'autosave de la session attend.
      // ⚠️ **Réécrit au rapatriement de `pre-prod` dans #1327.** La version de
      // #1404 balayait l'instantané de `sessionAdapter` pour retrouver la
      // session portant un `documentSessionId`. L'adaptateur mono-session est
      // retiré (#1334) et la conversation EST la clé (#1336) : le registre
      // répond directement, sans balayage ni second nom de la même chose.
      // La fusion des deux branches ne produisait AUCUN conflit ici — seul le
      // type-check l'a révélé.
      isSessionWriting: (conversationId, options) => {
        const session = this.registry.getSession(conversationId);
        return session !== null && (
          session.streamCount > 0 || session.writerCount > 0
          || (!options?.afterCompletedAgentMutation && session.mutationCount > 0)
        );
      },
    });
    this.capacity = new CanvasResidenceCapacityCoordinator({
      getRegistrySnapshot: () => this.registry.getSnapshot(),
      getScopeSnapshot: (conversationId) => this.directory.get(conversationId)?.getSnapshot() ?? null,
      getDraftController: (conversationId) => this.directory.getDraftController(conversationId),
      canReuseOriginalSeed: (conversationId) => this.peutReprendreLaSourceInitiale(conversationId),
      openingNeverBecameReady: (conversationId) => this.louvertureNaJamaisEtePrete(conversationId),
      getLogicalSeed: (conversationId) => {
        const logical = this.directory.getLogicalSeed(conversationId);
        return logical ? {
          documentId: logical.identity.documentId,
          presentation: logical.presentation,
        } : null;
      },
      transition: (conversationId, transition) => (
        this.registry.transitionSession(conversationId, transition)
      ),
      touch: (conversationId) => this.registry.touchSession(conversationId),
      mountColdTarget: (input) => this.mountColdTarget(input.conversationId, input.seed),
      coolPhysical: (conversationId, reason) => this.directory.cool(conversationId, reason),
      waitForExactReady: (identity, timeoutMs) => this.waitForExactScopeReady(identity, timeoutMs),
      selectVisible: (conversationId) => { this.registry.selectVisibleSession(conversationId); },
      maskVisible: (conversationId) => { this.registry.maskSession(conversationId); },
    }, {
      ...options.capacity,
      loadDraftContent: options.capacity?.loadDraftContent
        ?? ((draftId) => canvasDraftApi.getDraftContent(draftId)),
    });
  }

  requestResident(request: CanvasResidentRequest): Promise<CanvasResidentDecision> {
    return this.capacity.requestResident(request);
  }

  /**
   * Ce document peut-il encore reprendre la source de son OUVERTURE, plutôt que
   * son brouillon durable ?
   *
   * ⚠️ **C'est l'interdiction que #1334 exige de faire survivre à une éviction
   * et à un crash d'éditeur** — l'énoncé « un document logique qui survit à son
   * éditeur ». Elle est portée par un `WeakSet` de CONTRÔLEURS, et c'est ce
   * choix qui la fait survivre : le contrôleur de brouillon vit dans le dossier
   * logique du directory, que `cool()` ne touche pas, et `releaseMount` (le
   * démontage qui suit un crash SuperDoc) ne dispose ni le scope ni le
   * contrôleur. Un `Set` de conversations aurait le même comportement ; un
   * drapeau posé sur le montage nullable, non — et c'est exactement le défaut
   * que le ticket nomme.
   *
   * Elle ne survit PAS à `dispose()`, qui frappe un contrôleur neuf. Le seul
   * appelant de `dispose` sur un fil vivant est la purge de compte, qui doit
   * précisément tout oublier ; `discardSessionForConversation` ne s'emploie que
   * sur un fil déjà supprimé en base.
   *
   * @returns `false` dès qu'un éditeur a été exposé ou qu'un brouillon a bougé —
   * et `false` aussi pour un contrôleur que ce runtime n'a jamais observé, un
   * directory injecté ou préparé pouvant précéder son observation : l'absence
   * d'édition constatée n'est pas la preuve d'une ouverture intacte.
   */
  peutReprendreLaSourceInitiale(conversationId: string): boolean {
    const controller = this.directory.getDraftController(conversationId);
    return controller !== null
      && this.observedDraftControllers.has(controller)
      && !this.seedReuseForbidden.has(controller);
  }

  /**
   * Aucun montage du document de cette conversation n'est devenu prêt, et son
   * brouillon n'a jamais bougé — qu'un éditeur ait été exposé ou non.
   *
   * Plus faible que `peutReprendreLaSourceInitiale`, et c'est voulu : elle
   * n'autorise PAS à rejouer la source, seulement à monter autre chose à sa
   * place (`CanvasResidentRequest.seedReplacesUnmountedOpening`). Même repli
   * prudent : un contrôleur jamais observé rend `false`.
   */
  louvertureNaJamaisEtePrete(conversationId: string): boolean {
    const controller = this.directory.getDraftController(conversationId);
    return controller !== null
      && this.observedDraftControllers.has(controller)
      && !this.aPuRecevoirDuTravail.has(controller);
  }

  pinSession(
    conversationId: string,
    token: { readonly kind: 'job' | 'lease'; readonly id: string },
  ): () => void {
    if (this.exclusiveReplacements.has(conversationId)) {
      throw new Error(
        `Canvas pin refused for conversationId=${conversationId}: an exact manual replacement is in progress. `
        + 'Retry the documentary job after the lawyer finishes replacing this document.',
      );
    }
    // Le registre DÉDUPLIQUE les jetons textuels : deux travaux qui réutilisent
    // le même jeton d'appelant (`job:<conversationId>`, p. ex. une génération et
    // un chargement du même tour) ne compteraient qu'UNE épingle, et la première
    // libération rendrait la session déprotégée alors qu'un travail court encore
    // (#1354/D10). Le suffixe monotone rend le compte physique exact — il doit
    // s'incrémenter À CHAQUE épingle, sinon deux épingles simultanées partagent
    // encore la même valeur et la déduplication reprend en silence.
    this.nextActivityToken += 1;
    const release = this.registry.pinSession(
      conversationId,
      `${token.kind}:${token.id}:${this.nextActivityToken}`,
    );
    if (!release) {
      throw new Error(
        `Canvas pin refused for conversationId=${conversationId} token=${token.kind}:${token.id}: `
        + 'logical session not found. Acquire the document of that exact conversation before starting its job.',
      );
    }
    const runtimePin = () => {
      this.livePins.delete(runtimePin);
      release();
    };
    this.livePins.set(runtimePin, conversationId);
    return runtimePin;
  }

  beginActivity(conversationId: string, kind: 'stream' | 'writer' | 'mutation'): () => void {
    const release = this.tryBeginActivity(conversationId, kind);
    if (!release) {
      throw new Error(
        `Canvas ${kind} activity refused for conversationId=${conversationId}: logical session not found, `
        + 'or an exact manual replacement holds it. Route the operation through its exact registered '
        + 'conversation and retry once the replacement has finished.',
      );
    }
    return release;
  }

  tryBeginActivity(
    conversationId: string,
    kind: 'stream' | 'writer' | 'mutation',
  ): (() => void) | null {
    if (this.exclusiveReplacements.has(conversationId)) return null;
    this.nextActivityToken += 1;
    return this.registry.beginActivity(
      conversationId,
      kind,
      `${kind}:${this.nextActivityToken}`,
    );
  }

  /**
   * Reserves the exact resident for a destructive human replacement.
   *
   * The reservation closes the race where a documentary job pins the same
   * session while a draft DELETE is in flight. Agent pin/activity acquisition
   * fails closed until the returned idempotent release is called.
   */
  tryBeginExclusiveReplacement(
    conversationId: string,
    requestingPin?: () => void,
  ): CanvasExclusiveReplacementLease | null {
    if (this.exclusiveReplacements.has(conversationId)) return null;
    // L'ÉPINGLE DOIT ÊTRE AUTHENTIQUE (#1354/D10). `livePins` ne contient que
    // les poignées que ce runtime a lui-même frappées : un appelant ne peut donc
    // pas se forger une exemption en passant une fonction quelconque. La clé est
    // la CONVERSATION depuis #1336 — c'est elle qui identifie le document.
    if (requestingPin && this.livePins.get(requestingPin) !== conversationId) return null;
    const session = this.registry.getSession(conversationId);
    // The requesting turn's own authentic pin protects the transaction; it is
    // the only protection ignored for admission and remains held throughout.
    const protectionsWithoutRequester = session && requestingPin
      ? { ...session, pinCount: session.pinCount - 1 }
      : session;
    if (
      !session
      || !protectionsWithoutRequester
      || isCanvasSessionProtected(protectionsWithoutRequester)
      || (requestingPin && session.pinCount !== 1)
    ) return null;
    this.nextActivityToken += 1;
    const leaseId = this.nextActivityToken;
    this.exclusiveReplacements.set(conversationId, leaseId);
    const releaseActivity = this.registry.beginActivity(
      conversationId,
      'mutation',
      `exclusive-replacement:${this.nextActivityToken}`,
    );
    if (!releaseActivity) {
      this.exclusiveReplacements.delete(conversationId);
      return null;
    }
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      releaseActivity();
      if (this.exclusiveReplacements.get(conversationId) === leaseId) {
        this.exclusiveReplacements.delete(conversationId);
      }
    };
    return Object.assign(release, { conversationId, leaseId, release });
  }

  async runWithActivity<T>(
    conversationId: string,
    kind: 'stream' | 'writer' | 'mutation',
    operation: () => Promise<T> | T,
    options: CanvasActivityOptions = {},
  ): Promise<T> {
    const release = this.beginActivity(conversationId, kind);
    try {
      // Announce activity BEFORE waiting: autosave cannot start another export
      // in the gap, and the existing export completes without rejecting a write.
      const controller = this.getDraftController(conversationId);
      if (kind !== 'stream' && controller?.hasExportInFlight()) {
        const startedAt = Date.now();
        this.#reportExportWait(options.onExportWait, { stage: 'started' });
        await controller.waitForExports();
        this.#reportExportWait(options.onExportWait, {
          stage: 'completed',
          durationMs: Math.max(0, Date.now() - startedAt),
        });
      }
      return await operation();
    } finally {
      release();
    }
  }

  /** An observer is diagnostic-only: it cannot reject, delay, or cancel a writer. */
  #reportExportWait(
    observer: CanvasActivityOptions['onExportWait'],
    event: CanvasExportWaitEvent,
  ): void {
    try {
      observer?.(event);
    } catch {
      // A browser diagnostic must never turn an already-safe export wait into a failed write.
    }
  }

  onSessionReady(listener: (session: CanvasRuntimeSession) => void): () => void {
    this.readyListeners.add(listener);
    return () => this.readyListeners.delete(listener);
  }

  onSessionSettled(listener: (session: CanvasRuntimeSession) => void): () => void {
    this.settledListeners.add(listener);
    return () => this.settledListeners.delete(listener);
  }

  /**
   * Le transport de chat s'abonne ici pour ne libérer le tour agent qu'après la
   * file réelle d'écriture. Le runtime conserve aussi le fait : si le terminal
   * SSE arrive dans la micro-fenêtre qui suit `auRepos()`, il ne doit pas être
   * perdu et laisser le backend attendre son timeout.
   */
  onStreamInsertionSettled(listener: (event: CanvasStreamInsertionSettled) => void): () => void {
    this.streamInsertionSettledListeners.add(listener);
    return () => this.streamInsertionSettledListeners.delete(listener);
  }

  /** Le flux donné a-t-il déjà terminé son insertion sur le montage vivant ? */
  wasStreamInsertionSettled(conversationId: string, jobId: string): boolean {
    const session = this.getSessionForConversation(conversationId);
    return session !== null && this.settledStreamInsertions.has(
      this.streamInsertionKey({ conversationId, mountEpoch: session.mountEpoch, jobId }),
    );
  }

  /**
   * Reçoit la seule preuve valide de fin d'insertion : `RedactionCanvas` a
   * observé la transition streamée puis attendu `PointDEcritureCanvas.auRepos`.
   * Un événement d'un ancien montage ne peut pas réveiller un nouveau Canvas.
   */
  signalStreamInsertionSettled(event: CanvasStreamInsertionSettled): void {
    const session = this.getSessionForConversation(event.conversationId);
    if (!session || session.state !== 'ready' || session.mountEpoch !== event.mountEpoch) return;
    const key = this.streamInsertionKey(event);
    if (this.settledStreamInsertions.has(key)) return;
    this.settledStreamInsertions.add(key);
    for (const listener of [...this.streamInsertionSettledListeners]) {
      try { listener({ ...event }); } catch { /* transport listener isolation */ }
    }
  }

  /**
   * Réserve l'identité logique du document d'une conversation, sans créer de
   * host ni d'instance SuperDoc.
   *
   * ⚠️ **Un seul verbe d'acquisition** (#1334). Il en existait deux —
   * `acquireForConversation` et `acquireManualForConversation` — aux corps
   * identiques jusque dans l'adaptateur qu'ils traversaient. Le geste agent et
   * le geste humain réservent exactement la même chose ; ce qui les distingue
   * est l'épingle de tour, que seul l'agent prend ensuite.
   */
  acquireForConversation(conversationId: string): CanvasSessionSnapshot {
    this.refuserUnFilEnCoursDeSuppression(conversationId);
    return this.registry.acquerirPourConversation(conversationId);
  }

  /**
   * LE seam de refus pendant une suppression (#1334).
   *
   * Toute opération documentaire d'un fil — les six entrées agent de
   * `WebSocketPool`, le geste humain d'ouverture, la restauration d'un
   * brouillon — commence par acquérir sa session ici. Poser le refus à ce seul
   * endroit le rend exhaustif ; le poser sur chaque appelant garantirait qu'un
   * septième appelant l'oublie.
   */
  private refuserUnFilEnCoursDeSuppression(conversationId: string): void {
    if (!estEnCoursDeSuppression(conversationId)) return;
    throw new Error(
      `Canvas document acquisition refused for conversationId=${conversationId}: `
      + 'this conversation is being deleted, so no new document work may start on it. '
      + 'Nothing was mounted and nothing was lost. Retry in another conversation, or wait '
      + 'for the deletion to fail — the thread and its document are kept when it does.',
    );
  }

  /**
   * Attache un brouillon restauré à sa session logique sans publier ses
   * métadonnées dans la projection visible avant l'acceptation de résidence.
   */
  prepareRestoredDraftSession(draft: CanvasDraft): CanvasSessionSnapshot {
    if (!draft.conversationId) {
      throw new Error(
        `Canvas draft restoration refused for draftId=${draft.id}: conversationId is null. `
        + 'Open this draft explicitly from the canvas menu instead of restoring it from a conversation.',
      );
    }
    const session = this.acquireForConversation(draft.conversationId);
    const presentation = presentationDUnBrouillonRestaure(draft);
    const controller = this.directory.prepareLogicalSession({
      identity: {
        conversationId: session.conversationId,
        mountEpoch: session.mountEpoch,
        documentId: draft.sourceDocumentId,
      },
      presentation,
      draftContext: {
        conversationId: draft.conversationId,
        caseId: draft.caseId,
        kind: draft.kind,
        title: draft.title,
        sourceDocumentId: draft.sourceDocumentId,
      },
    });
    const ownerDecision = controller.restoreDraft(
      draft.id,
      draft.conversationId,
      draft.integrityAttemptId,
    );
    if (!ownerDecision.accepted) {
      throw new Error(
        `Canvas draft restoration owner mismatch for draftId=${draft.id} `
        + `conversationId=${draft.conversationId}. `
        + 'Purge the stale logical session and retry from its owning conversation.',
      );
    }
    return session;
  }

  /**
   * Le montage vivant du document d'une conversation, ou `null`.
   *
   * ⚠️ **C'est le SEUL lecteur d'un montage, et `getCurrentSession()` a
   * disparu** (#1336, critère d'acceptation 4). Cette dernière projetait la
   * sélection visible et servait de destination par défaut à qui n'en nommait
   * pas — un routage implicite vers « le document qu'on regarde ». Depuis que la
   * conversation EST la clé, tout appelant en connaît une : il n'y a plus de
   * raison de deviner.
   *
   * ⚠️ **La résolution passe par le DIRECTORY, plus par le registre.** Interroger
   * `registry.getSession(conversationId)` avant `directory.get(conversationId)`
   * était devenu une tautologie : les deux collections sont indexées par la même
   * clé, un scope ne naît qu'après acquisition et `discardSessionForConversation`
   * les vide ensemble. La question posée ici est physique — « ce document est-il
   * monté ? » —, et seul le directory y répond.
   */
  getSessionForConversation(conversationId: string): CanvasRuntimeSession | null {
    const snapshot = this.directory.get(conversationId)?.getSnapshot();
    return snapshot ? toRuntimeSession(snapshot) : null;
  }

  /**
   * Monte le document d'une conversation acquise, pour un TEST ou une STORY.
   *
   * ⚠️ **Jamais pour la production** (#1338) — un test de structure
   * (`canvasDocumentRuntime.montageDeTest.test.ts`) refuse tout appelant hors
   * tests et stories. La production n'a que deux entrées de montage :
   * `requestResident` (le premier montage, avec capacité, graine et attente du
   * ready) et `replaceDocument` (le remplacement exact d'un résident). Une
   * troisième entrée de production contournerait la capacité : un éditeur de
   * plus que le plafond, ou un document monté sans sa graine exacte.
   *
   * Le corps est celui du montage à froid de la capacité, pas une copie : une
   * fixture qui monterait par un chemin parallèle pourrait diverger du vrai
   * montage sans qu'aucun test ne le voie. Elle y ajoute la sélection visible que
   * fait le geste d'ouverture de l'avocat, et saute ce que la capacité fait
   * autour : pas de victime, pas de passage `hydrating`, pas d'attente du ready.
   * Le montage rendu est donc `loading` tant que le test ne publie pas ses
   * signaux SuperDoc.
   *
   * @throws si la conversation n'a pas été acquise (`acquireForConversation`).
   */
  mountForTests(
    input: { readonly conversationId: string } & Pick<CanvasResidentSeed, 'documentId'>,
  ): CanvasRuntimeSession {
    this.registry.selectSession(input.conversationId);
    return this.mountColdTarget(input.conversationId, {
      documentId: input.documentId,
      presentation: null,
    });
  }

  /**
   * Commits one exact document replacement or leaves the existing resident
   * untouched. Callers must finish any asynchronous download before entering
   * this synchronous boundary; an abort or stale identity is refused before a
   * mount epoch, presentation, or physical SuperDoc resource can change.
   */
  replaceDocument(
    input: CanvasRuntimeDocumentReplacementInput,
  ): CanvasRuntimeDocumentReplacementDecision {
    if (input.signal?.aborted) return { accepted: false, reason: 'aborted' };
    const current = this.registry.getSession(input.identity.conversationId);
    if (!current) return { accepted: false, reason: 'session-not-found' };
    if (current.mountEpoch !== input.identity.mountEpoch) {
      return { accepted: false, reason: 'mount-epoch-mismatch' };
    }
    const scope = this.directory.get(input.identity.conversationId);
    if (!scope || scope.getSnapshot().state === 'disposed') {
      return { accepted: false, reason: 'session-not-mounted' };
    }
    const lease = input.replacementLease;
    const hasSuppliedLease = lease !== undefined
      && lease.conversationId === input.identity.conversationId
      && this.exclusiveReplacements.get(input.identity.conversationId) === lease.leaseId;
    if (isCanvasSessionProtected(current) && !hasSuppliedLease) {
      return { accepted: false, reason: 'session-protected' };
    }
    const acquiredLease = hasSuppliedLease
      ? null
      : this.tryBeginExclusiveReplacement(input.identity.conversationId);
    if (!hasSuppliedLease && !acquiredLease) return { accepted: false, reason: 'replacement-in-progress' };

    try {
      if (input.signal?.aborted) return { accepted: false, reason: 'aborted' };
      const remounted = this.registry.remountExactSession(input.identity);
      if (!remounted.accepted) return remounted;
      const session = this.mountScope({
        conversationId: remounted.session.conversationId,
        mountEpoch: remounted.session.mountEpoch,
        documentId: input.documentId,
      }, input.presentation);
      return {
        accepted: true,
        session,
        previousMountEpoch: input.identity.mountEpoch,
      };
    } finally {
      acquiredLease?.release();
    }
  }

  getSessionsSnapshot(): CanvasSessionRegistrySnapshot {
    return this.registry.getSnapshot();
  }

  /**
   * Ce qui occupe le document d'une conversation, en ce moment (#1586).
   *
   * Posé par la restauration et par l'amorce d'une admission agent AVANT de
   * préparer un brouillon : `getScope` seul ne voit ni un fil épinglé ou en
   * activité avant son montage, ni une transaction de résidence encore sans
   * scope, ni un remplacement exclusif. Les causes sont rendues TOUTES, dans un
   * ordre stable, parce qu'elles finissent dans un message de refus lu hors
   * contexte — et que l'appelant en trie deux familles : les causes physiques
   * (un éditeur existe ou va exister) et les protections d'un travail.
   *
   * @returns une liste vide pour un fil dont rien n'occupe le document.
   */
  decrireLOccupationDuDocument(conversationId: string): readonly CauseDOccupationDocumentaire[] {
    const causes: CauseDOccupationDocumentaire[] = [];
    if (this.directory.get(conversationId)) causes.push('monte');
    const session = this.registry.getSession(conversationId);
    if (session?.temperature === 'hot') causes.push('chaud');
    if (session?.temperature === 'hydrating') causes.push('hydratation');
    if (this.capacity.aUneTransactionEnVolPour(conversationId)) causes.push('transaction-de-residence');
    if (this.exclusiveReplacements.has(conversationId)) causes.push('remplacement-exclusif');
    if (session && session.pinCount > 0) causes.push('epingle');
    if (session && (session.streamCount > 0 || session.writerCount > 0 || session.mutationCount > 0)) {
      causes.push('activite');
    }
    return causes;
  }

  subscribeSessions(listener: (snapshot: CanvasSessionRegistrySnapshot) => void): () => void {
    return this.registry.subscribe(listener);
  }

  /**
   * Observe the instant a physical scope exists. The CanvasSlot uses this to
   * move the host to a slot that React rendered just before the cold mount.
   */
  subscribeScopeMount(listener: (conversationId: string) => void): () => void {
    this.scopeMountedListeners.add(listener);
    return () => this.scopeMountedListeners.delete(listener);
  }

  /**
   * Holds the generic UI fallback while an agent owns the exact cold DOCX seed.
   *
   * The returned closure is identity-bound: a stale operation cannot clear a
   * later reservation for the same conversation.
   */
  reserveColdMountForVisibleSlot(conversationId: string): () => void {
    if (!this.registry.getSession(conversationId)) {
      throw new Error(
        `Canvas cold-mount reservation refused for conversationId=${conversationId}: logical session=missing. `
        + 'Acquire the conversation before reserving its DOCX mount.',
      );
    }
    if (this.coldMountsAwaitingVisibleSlot.has(conversationId)) {
      throw new Error(
        `Canvas cold-mount reservation refused for conversationId=${conversationId}: another exact DOCX seed is already waiting for the assistant slot.`,
      );
    }
    const token = Symbol(conversationId);
    this.coldMountsAwaitingVisibleSlot.set(conversationId, token);
    return () => {
      if (this.coldMountsAwaitingVisibleSlot.get(conversationId) === token) {
        this.coldMountsAwaitingVisibleSlot.delete(conversationId);
      }
    };
  }

  /** True only while an agent has deferred this conversation's first mount for its visible slot. */
  hasColdMountReservedForVisibleSlot(conversationId: string): boolean {
    return this.coldMountsAwaitingVisibleSlot.has(conversationId);
  }

  selectVisibleSession(conversationId: string): CanvasRuntimeSession | null {
    const selected = this.registry.selectVisibleSession(conversationId);
    return selected ? this.getSessionForConversation(conversationId) : null;
  }

  maskSession(conversationId: string): CanvasRuntimeSession | null {
    const masked = this.registry.maskSession(conversationId);
    return masked ? this.getSessionForConversation(conversationId) : null;
  }

  /** Un callback incomplet ne peut jamais désigner un scope documentaire. */
  markReadySignal(
    identity: CanvasSessionCallbackIdentity,
    instance: SuperDocInstance,
  ): Promise<void> {
    const accepted = this.resolveCallbackIdentity(identity);
    if (!accepted) return Promise.resolve();
    const scope = this.directory.get(accepted.conversationId);
    if (!scope || scope.getSnapshot().state === 'disposed') return Promise.resolve();
    // SuperDoc can expose a usable editor on the instance before onEditorCreate.
    if (instance.activeEditor) this.seedReuseForbidden.add(scope.getDraftController());
    if (scope.incarneUnAutreEditeur(accepted.mountEpoch, { instance })) {
      this.consommerLEpoqueDUnEditeurNeuf(accepted);
      return Promise.resolve();
    }
    return scope.markReady(accepted.mountEpoch, instance);
  }

  markEditorSignal(
    identity: CanvasSessionCallbackIdentity,
    editor: SuperDocRuntimeEditor,
  ): Promise<void> {
    const accepted = this.resolveCallbackIdentity(identity);
    if (!accepted) return Promise.resolve();
    const scope = this.directory.get(accepted.conversationId);
    if (!scope || scope.getSnapshot().state === 'disposed') return Promise.resolve();
    // The physical editor may accept edits before doc.info exposes a revision.
    // Record this before publishing it to physical-resource listeners.
    this.seedReuseForbidden.add(scope.getDraftController());
    if (scope.incarneUnAutreEditeur(accepted.mountEpoch, { editor })) {
      this.consommerLEpoqueDUnEditeurNeuf(accepted);
      return Promise.resolve();
    }
    return scope.markEditor(accepted.mountEpoch, editor);
  }

  /**
   * Attend que le montage `expected` soit prêt, ou le refuse dès qu'il ne peut
   * plus l'être : supplanté, porteur d'un autre document, ou en échec.
   */
  waitForReady(
    expected: CanvasReadyExpectation,
    timeoutMs: number,
  ): Promise<CanvasRuntimeReadySession> {
    const verdict = this.readyMatch(expected);
    if (verdict.issue === 'pret') return Promise.resolve({ ...verdict.session });
    if (verdict.issue === 'refuse') return Promise.reject(new Error(verdict.raison));

    return new Promise((resolve, reject) => {
      const waiter: ReadyWaiter = {
        expected,
        timeoutId: setTimeout(() => {
          this.waiters.delete(waiter);
          reject(new Error(this.describeWaitFailure(expected, timeoutMs)));
        }, timeoutMs),
        resolve,
        reject,
      };
      this.waiters.add(waiter);
    });
  }

  async attendreLaPromotionEnCours(conversationId: string): Promise<void> {
    await this.directory.get(conversationId)?.waitForPromotion();
  }

  getScope(conversationId: string): CanvasDocumentScope | null {
    return this.directory.get(conversationId);
  }

  getDraftController(conversationId: string): CanvasDraftSessionController | null {
    return this.directory.getDraftController(conversationId);
  }

  requireReady(conversationId: string): CanvasRuntimeSession {
    const snapshot = this.directory.requireReady(conversationId);
    const runtime = toRuntimeSession(snapshot);
    if (!runtime) {
      throw new Error(`Canvas document scope unexpectedly disposed for conversationId=${conversationId}.`);
    }
    return runtime;
  }

  async enqueueMutation<T>(conversationId: string, operation: () => Promise<T> | T): Promise<T> {
    const scope = this.directory.get(conversationId);
    if (!scope) {
      throw new Error(
        `Canvas mutation refused for conversationId=${conversationId}: scope=missing. `
        + 'Mount that exact routed session before enqueueing its mutation.',
      );
    }
    return this.runWithActivity(
      conversationId,
      'mutation',
      () => scope.enqueueMutation(operation),
    );
  }

  refreshRevision(conversationId: string, revision: string): CanvasRuntimeSession | null {
    const snapshot = this.directory.get(conversationId)?.refreshRevision(revision) ?? null;
    return snapshot ? toRuntimeSession(snapshot) : null;
  }

  async syncRevisionFromEditor(conversationId: string): Promise<string | null> {
    return this.directory.get(conversationId)?.syncRevisionFromEditor() ?? null;
  }


  /**
   * Retire le document logique ET son montage pour UN fil supprimé (#1334).
   *
   * Deux choses à ne pas défaire. La cible est résolue par CONVERSATION, jamais
   * par la sélection visible : le fil supprimé peut être parqué derrière un
   * autre document. Et **aucune protection n'est consultée ici**, contrairement
   * à toutes les autres portes du runtime — la garde de suppression l'a déjà
   * fait AVANT le DELETE, et à ce stade la conversation n'existe plus en base :
   * refuser laisserait un document orphelin occuper une place de résidence.
   * La session est retirée du REGISTRE, ce qui fait répondre `session-not-found`
   * à tout callback de montage tardif.
   *
   * L'ordre est celui de la purge de compte : contrôleur de brouillon d'abord
   * (`directory.dispose`), registre ensuite.
   *
   * @returns `false` si ce fil ne portait aucun document — cas courant d'une
   * conversation purement textuelle, et pas une erreur.
   */
  discardSessionForConversation(conversationId: string, reason: string): boolean {
    if (!this.registry.getSession(conversationId)) return false;
    this.coldMountsAwaitingVisibleSlot.delete(conversationId);
    this.directory.dispose(conversationId, reason);
    // Les clés sont `<conversationId>#<mountEpoch>` : retirer la seule chaîne
    // nue ne supprimerait rien, et un montage réglé survivrait à son document.
    for (const mountKey of this.settledMounts) {
      if (mountKey.startsWith(`${conversationId}#`)) this.settledMounts.delete(mountKey);
    }
    for (const insertionKey of this.settledStreamInsertions) {
      if (insertionKey.startsWith(`${conversationId}#`)) this.settledStreamInsertions.delete(insertionKey);
    }
    this.exclusiveReplacements.delete(conversationId);
    return this.registry.deleteSession(conversationId);
  }

  purgeAllSessions(reason: string): number {
    this.rejectWaiters(reason);
    this.capacity.reset();
    this.exclusiveReplacements.clear();
    this.coldMountsAwaitingVisibleSlot.clear();
    this.livePins = new WeakMap();
    const removed = this.directory.purgeAll(reason);
    this.registry.purgeSessions();
    this.settledMounts.clear();
    this.settledStreamInsertions.clear();
    return removed;
  }

  resetForTests(): void {
    this.rejectWaiters('CanvasDocumentRuntimeCoordinator reset while a test waiter was pending.');
    this.capacity.reset();
    this.exclusiveReplacements.clear();
    this.livePins = new WeakMap();
    this.readyListeners.clear();
    this.settledListeners.clear();
    this.streamInsertionSettledListeners.clear();
    this.scopeMountedListeners.clear();
    this.coldMountsAwaitingVisibleSlot.clear();
    this.directory.purgeAll('reset test');
    this.registry.purgeSessions();
    this.settledMounts.clear();
    this.settledStreamInsertions.clear();
  }

  /** Le premier montage d'un document acquis : une époque neuve, puis son scope. */
  private mountColdTarget(conversationId: string, seed: CanvasResidentSeed): CanvasRuntimeSession {
    const identity = this.registry.mountColdSession(conversationId);
    if (!identity) {
      throw new Error(
        `Canvas cold mount refused for conversationId=${conversationId}: `
        + 'no logical document is registered for this conversation. Acquire it '
        + '(acquireForConversation) before requesting its residence.',
      );
    }
    const mounted = this.mountScope({
      conversationId: identity.conversationId,
      mountEpoch: identity.mountEpoch,
      documentId: seed.documentId,
    });
    const scope = this.directory.get(conversationId);
    if (scope && seed.presentation) scope.capturePresentation(seed.presentation);
    if (scope) scope.markColdHydratedDraft(seed.hydratedDraftId ?? null);
    return mounted;
  }

  private mountScope(
    identity: CanvasDocumentMountIdentity,
    presentation?: CanvasDocumentPresentationSnapshot,
  ): CanvasRuntimeSession {
    const previousController = this.directory.getDraftController(identity.conversationId);
    if (previousController && !this.observedDraftControllers.has(previousController)) {
      // An injected/prepared directory can predate this runtime's observation;
      // absence of observed edits is not proof of an untouched opening.
      this.seedReuseForbidden.add(previousController);
      this.aPuRecevoirDuTravail.add(previousController);
    }
    const scope = this.directory.mount(identity, presentation);
    // This fires in the capacity transaction, before CanvasResidence can render
    // the portal containing SuperDoc. A pre-existing assistant slot therefore
    // receives the host before SuperDoc measures its initial container.
    for (const listener of [...this.scopeMountedListeners]) listener(identity.conversationId);
    this.observeScope(scope);
    const mounted = toRuntimeSession(scope.getSnapshot());
    if (!mounted) throw new Error(`Canvas scope mount disposed unexpectedly: conversationId=${identity.conversationId}.`);
    this.flushWaiters();
    return mounted;
  }

  private observeScope(scope: CanvasDocumentScope): void {
    if (this.observedScopes.has(scope)) return;
    this.observedScopes.add(scope);
    scope.subscribeSnapshot((snapshot) => this.handleScopeSnapshot(snapshot));
    const draftController = scope.getDraftController();
    if (!this.observedDraftControllers.has(draftController)) {
      this.observedDraftControllers.add(draftController);
      draftController.subscribe(() => this.projectCheckpoint(draftController));
      this.projectCheckpoint(draftController);
    }
  }

  private projectCheckpoint(controller: CanvasDraftSessionController): void {
    const snapshot = controller.getSnapshot();
    if (snapshot.draftId !== null || snapshot.version > 0 || snapshot.savedVersion > 0 || snapshot.status !== 'idle') {
      this.seedReuseForbidden.add(controller);
      this.aPuRecevoirDuTravail.add(controller);
    }
    // Un contrôleur sans propriétaire ne projette rien : depuis #1336 la
    // conversation EST la clé du registre, et il n'existe aucune session à
    // transitionner tant qu'elle est nulle.
    if (snapshot.conversationId === null) return;
    const checkpoint = snapshot.status === 'saving'
      ? 'checkpointing'
      : snapshot.status === 'error'
        ? 'error'
        : snapshot.status === 'unsaved'
          ? 'dirty'
          : 'clean';
    this.registry.transitionSession(snapshot.conversationId, {
      axis: 'checkpoint',
      value: checkpoint,
    });
  }

  private handleScopeSnapshot(snapshot: CanvasDocumentScopeSnapshot): void {
    if (snapshot.editor !== null || snapshot.instance?.activeEditor || snapshot.state === 'ready') {
      const controller = this.directory.getDraftController(snapshot.conversationId);
      if (controller) {
        this.seedReuseForbidden.add(controller);
        // `ready` seul : un éditeur exposé sans révision lisible n'a jamais été
        // écrivable, il ne ferme que la reprise de la source.
        if (snapshot.state === 'ready') this.aPuRecevoirDuTravail.add(controller);
      }
    }
    if (snapshot.state !== 'ready' && snapshot.state !== 'failed') return;
    const mountKey = cleDunMontageRegle(snapshot);
    if (this.settledMounts.has(mountKey)) return;
    this.settledMounts.add(mountKey);
    if (snapshot.state === 'ready') {
      this.registry.markMountHot({
        conversationId: snapshot.conversationId,
        mountEpoch: snapshot.mountEpoch,
      });
    }
    this.flushWaiters();
    const session = toRuntimeSession(snapshot);
    if (!session) return;
    for (const listener of [...this.settledListeners]) {
      try { listener({ ...session }); } catch { /* isolate lifecycle listeners */ }
    }
    if (session.state === 'ready') {
      for (const listener of [...this.readyListeners]) {
        try { listener({ ...session }); } catch { /* isolate transport listeners */ }
      }
    }
  }

  private streamInsertionKey(event: CanvasStreamInsertionSettled): string {
    return `${event.conversationId}#${event.mountEpoch}#${event.jobId}`;
  }

  /**
   * D1 de #1337 (confirmée par le porteur le 2026-09-10) : un éditeur neuf est un
   * montage neuf, donc il consomme une époque.
   *
   * Le document est remonté À L'IDENTIQUE — même source, même présentation —
   * sous une époque neuve : `remount` invalide la file de mutations, détruit
   * l'instance liée, publie la nouvelle identité dans le DOM, et le panneau, dont
   * la clé React de l'éditeur est cette époque, monte un hôte neuf qui s'y lie.
   * La ressource qui a déclenché le remontage n'est PAS liée : son hôte est celui
   * que la clé va démonter, et ses callbacks tardifs portent l'époque dépassée,
   * que `decideCallback` refuse. Une seule époque est donc consommée — le premier
   * éditeur de l'hôte neuf est une première liaison.
   *
   * Aucune protection n'est consultée, et c'est délibéré : un éditeur a changé
   * sous une épingle ou un flux, le nier ne le ferait pas revenir. Ce sont
   * justement les gardes d'époque — mutation en file, ré-assertion après `await`,
   * liaison d'un travail admis — qui doivent alors refuser.
   */
  private consommerLEpoqueDUnEditeurNeuf(identity: CanvasSessionCallbackIdentity): void {
    const remonte = this.registry.remountExactSession(identity);
    if (!remonte.accepted) return;
    const scope = this.directory.get(identity.conversationId);
    if (!scope) return;
    this.mountScope({
      conversationId: identity.conversationId,
      mountEpoch: remonte.session.mountEpoch,
      documentId: scope.getSnapshot().documentId,
    });
  }

  private resolveCallbackIdentity(
    identity: CanvasSessionCallbackIdentity,
  ): CanvasSessionCallbackIdentity | null {
    const decision = this.registry.decideCallback(identity);
    return decision.accepted ? identity : null;
  }

  /**
   * Le verdict d'une attente sur le montage vivant de sa conversation.
   *
   * ⚠️ **Refuser dès que le montage attendu ne PEUT plus devenir prêt**, au lieu
   * de laisser courir le délai : une époque n'est jamais réémise, donc un
   * montage supplanté ne reviendra pas, et un montage exact qui porte un autre
   * document ne changera pas de document. Le délai ne reste que pour ce qui peut
   * encore arriver — des signaux SuperDoc en retard, ou un scope absent.
   *
   * ⚠️ **Dépendance à D1** (#1337, confirmée par le porteur le 2026-09-10) : sous
   * D1, un éditeur que SuperDoc reconstruit sous l'hôte du montage attendu
   * consomme une époque — le montage est alors supplanté ici, et l'attente
   * refusée (chargement d'une pièce, admission d'un travail). Sans D1, le même
   * éditeur reconstruit resterait sous l'époque attendue et l'attente se
   * résoudrait. Garde : « D1 × D3 » de `canvasDocumentRuntime.test.ts`.
   */
  private readyMatch(
    expected: CanvasReadyExpectation,
  ):
    | { readonly issue: 'pret'; readonly session: CanvasRuntimeReadySession }
    | { readonly issue: 'refuse'; readonly raison: string }
    | { readonly issue: 'attendre' } {
    const session = this.getSessionForConversation(expected.conversationId);
    if (!session) return { issue: 'attendre' };
    if (session.mountEpoch !== expected.mountEpoch) {
      return {
        issue: 'refuse',
        raison: `Canvas document wait refused for conversationId=${expected.conversationId}: `
          + `the awaited mountEpoch=${expected.mountEpoch} was superseded by mountEpoch=${session.mountEpoch} `
          + 'before becoming ready (another remount, or an editor SuperDoc rebuilt under its host). The document '
          + 'this wait was armed for will never be ready; do not acknowledge it — retry from the current mount.',
      };
    }
    if (session.documentId !== expected.documentId) {
      return {
        issue: 'refuse',
        raison: `Canvas document wait refused for conversationId=${expected.conversationId} `
          + `mountEpoch=${expected.mountEpoch}: this mount carries documentId=${session.documentId ?? 'none'}, `
          + `but documentId=${expected.documentId ?? 'none'} was requested. The replacement mounted another `
          + 'document than the one asked for; do not acknowledge it — re-inspect the canvas, then retry the load.',
      };
    }
    if (session.state === 'failed') {
      return { issue: 'refuse', raison: this.describeFailedRevision(expected, session.failure) };
    }
    if (session.state === 'ready' && session.revision) {
      return { issue: 'pret', session: { ...session, revision: session.revision } };
    }
    return { issue: 'attendre' };
  }

  private flushWaiters(): void {
    for (const waiter of [...this.waiters]) {
      const verdict = this.readyMatch(waiter.expected);
      if (verdict.issue === 'attendre') continue;
      clearTimeout(waiter.timeoutId);
      this.waiters.delete(waiter);
      if (verdict.issue === 'pret') waiter.resolve({ ...verdict.session });
      else waiter.reject(new Error(verdict.raison));
    }
  }

  private rejectWaiters(reason: string): void {
    for (const waiter of [...this.waiters]) {
      clearTimeout(waiter.timeoutId);
      this.waiters.delete(waiter);
      waiter.reject(new Error(
        `Canvas document wait abandoned before ready: ${reason} (expected ${this.decrireLAttendu(waiter.expected)}).`,
      ));
    }
  }

  private decrireLAttendu(expected: CanvasReadyExpectation): string {
    return `conversationId=${expected.conversationId} mountEpoch=${expected.mountEpoch} `
      + `documentId=${expected.documentId ?? 'none'}`;
  }

  private describeFailedRevision(expected: CanvasReadyExpectation, failure: string | null): string {
    return (
      `Canvas document load failed before ready: ${this.decrireLAttendu(expected)} `
      + 'received current onReady/onEditorCreate, but the document revision was not readable. '
      + `Cause: ${failure ?? 'not recorded'}. `
      + 'The revision is read from editor.doc.info({}), then from editor.doc.blocks.list() when info throws. '
      + 'Do not acknowledge load_document_into_canvas; remount the canvas or retry after SuperDoc exposes a revision.'
    );
  }

  private describeWaitFailure(expected: CanvasReadyExpectation, timeoutMs: number): string {
    const current = this.getSessionForConversation(expected.conversationId);
    const summary = current
      ? `target={conversationId:${current.conversationId}, mountEpoch:${current.mountEpoch}, state:${current.state}, hasInstance:${Boolean(current.instance)}, hasEditor:${Boolean(current.editor)}, revision:${current.revision ?? 'null'}}`
      : `target={conversationId:${expected.conversationId}, state:missing}`;
    return (
      `Timed out after ${timeoutMs}ms waiting for canvas document ready: `
      + `expected ${this.decrireLAttendu(expected)}; ${summary}; ${decrireLeChargementDeLEditeur()}. `
      + 'Ready requires the exact mount, onReady, onEditorCreate, and a readable document revision (editor.doc.info({}), or editor.doc.blocks.list() when info throws).'
    );
  }

  private waitForExactScopeReady(
    identity: CanvasDocumentMountIdentity,
    timeoutMs: number,
  ): Promise<CanvasDocumentScopeSnapshot> {
    const scope = this.directory.get(identity.conversationId);
    if (!scope || scope.getSnapshot().mountEpoch !== identity.mountEpoch) {
      return Promise.reject(new Error(
        `Canvas hydration wait refused for conversationId=${identity.conversationId} `
        + `mountEpoch=${identity.mountEpoch}: exact physical scope is missing. Retry the residence request.`,
      ));
    }
    const current = scope.getSnapshot();
    if (current.state === 'ready') return Promise.resolve(current);
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (outcome: { snapshot: CanvasDocumentScopeSnapshot } | { error: Error }) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        unsubscribe();
        if ('error' in outcome) reject(outcome.error);
        else resolve(outcome.snapshot);
      };
      const unsubscribe = scope.subscribeSnapshot((snapshot) => {
        if (snapshot.mountEpoch !== identity.mountEpoch || snapshot.state === 'disposed') {
          finish({ error: new Error(
            `Canvas hydration became stale for conversationId=${identity.conversationId}: `
            + `expectedMountEpoch=${identity.mountEpoch} liveMountEpoch=${snapshot.mountEpoch} `
            + `state=${snapshot.state}. Retry the same conversation.`,
          ) });
        } else if (snapshot.state === 'failed') {
          finish({ error: new Error(
            `Canvas hydration failed for conversationId=${identity.conversationId} `
            + `mountEpoch=${identity.mountEpoch}: SuperDoc did not expose a revision. `
            + `Cause: ${snapshot.failure ?? 'not recorded'}. Retry the same conversation.`,
          ) });
        } else if (snapshot.state === 'ready') finish({ snapshot });
      });
      const timeoutId = setTimeout(() => finish({ error: new Error(
        this.describeWaitFailure(identity, timeoutMs),
      ) }), timeoutMs);
    });
  }
}

function developmentCapacityOverride(): number | undefined {
  if (!import.meta.env.DEV || typeof window === 'undefined') return undefined;
  const candidate = (window as unknown as {
    __HECTOR_CANVAS_RESIDENT_CAPACITY_TEST__?: unknown;
  }).__HECTOR_CANVAS_RESIDENT_CAPACITY_TEST__;
  return typeof candidate === 'number' && Number.isInteger(candidate) && candidate > 0
    ? candidate
    : undefined;
}

export const canvasDocumentRuntime = new CanvasDocumentRuntimeCoordinator({
  registry: canvasSessionRegistry,
  // Playwright reduces the limit before app bootstrap to exercise one cold
  // exchange with only A/B. Production keeps the coordinator default (3).
  capacity: { maxHotSessions: developmentCapacityOverride() },
});
